import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Lazy — invoked ONLY when the tab is active, so a screen's cards fetch on demand, not all at once. */
  render: () => ReactNode;
}

/**
 * Accessible tab strip (WAI-ARIA tablist): roving tabindex, `aria-selected`, RTL-correct arrow keys
 * (Right/Left follow the document direction), Home/End. Renders ONLY the active panel — the whole point is
 * that a long, card-stacked screen no longer mounts (and fetches) everything at once. The selected tab
 * persists per screen via `storageKey` (localStorage, `ct.tab.*`), so returning to a screen reopens where you
 * left off. Purely presentational: it groups EXISTING cards, holding no domain state of its own.
 */
export function Tabs({
  items,
  storageKey,
  initialId,
  forceId,
}: {
  items: TabItem[];
  storageKey?: string;
  initialId?: string;
  /** A deep-link (e.g. an onboarding CTA) can FORCE a tab, overriding the sticky last-used one. Changing
   *  this to a valid id selects it; the caller resets it to null once applied so a repeat can re-fire. */
  forceId?: string | null | undefined;
}): ReactNode {
  const [active, setActive] = useState<string>(() => {
    const saved = storageKey ? localStorage.getItem(`ct.tab.${storageKey}`) : null;
    if (saved && items.some((t) => t.id === saved)) return saved;
    if (initialId && items.some((t) => t.id === initialId)) return initialId;
    return items[0]?.id ?? '';
  });

  const select = useCallback(
    (id: string) => {
      setActive(id);
      if (storageKey) localStorage.setItem(`ct.tab.${storageKey}`, id);
    },
    [storageKey],
  );

  // Apply an external force (deep-link) whenever it changes to a valid tab. Depends only on `forceId` — the
  // trigger — so a stable inline `items` array can't re-fire it every render and pin the tab.
  useEffect(() => {
    if (forceId && items.some((t) => t.id === forceId)) select(forceId);
  }, [forceId]);

  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKeyDown = (e: React.KeyboardEvent, idx: number) => {
    const rtl = document.documentElement.dir === 'rtl';
    let next: number;
    if (e.key === 'ArrowRight') next = rtl ? idx - 1 : idx + 1;
    else if (e.key === 'ArrowLeft') next = rtl ? idx + 1 : idx - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    else return;
    e.preventDefault();
    next = (next + items.length) % items.length;
    select(items[next]!.id);
    btnRefs.current[next]?.focus();
  };

  const activeItem = items.find((t) => t.id === active) ?? items[0];
  if (!activeItem) return null;

  return (
    <div className="tabs">
      <div className="tablist" role="tablist">
        {items.map((t, i) => {
          const selected = t.id === activeItem.id;
          return (
            <button
              key={t.id}
              ref={(el) => {
                btnRefs.current[i] = el;
              }}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`panel-${t.id}`}
              tabIndex={selected ? 0 : -1}
              className={selected ? 'tab tab-active' : 'tab'}
              onClick={() => select(t.id)}
              onKeyDown={(e) => onKeyDown(e, i)}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`panel-${activeItem.id}`} aria-labelledby={`tab-${activeItem.id}`}>
        {activeItem.render()}
      </div>
    </div>
  );
}
